import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'edit-fields',
  version: '3.4.0',
  properties: [
    {
      key: 'mode',
      label: 'Mode',
      type: 'options',
      required: true,
      default: 'manual',
      options: [
        { value: 'manual', name: 'Manual Mapping' },
        { value: 'json', name: 'JSON' },
      ],
      description: 'Choose how to define field operations',
    },

    // ===== MANUAL MODE =====
    {
      key: 'fields',
      label: 'Fields',
      type: 'fixedCollection',
      required: true,
      typeOptions: {
        multipleValues: true,
      },
      default: {
        values: [],
      },
      displayOptions: {
        show: { mode: ['manual'] },
      },
      fixedCollectionOptions: [
        {
          name: 'values',
          displayName: 'Field',
          values: [
            {
              key: 'operation',
              label: 'Operation',
              type: 'options',
              required: true,
              default: 'set',
              options: [
                { value: 'set', name: 'Set Value' },
                { value: 'rename', name: 'Rename Field' },
                { value: 'remove', name: 'Remove Field' },
                { value: 'convert', name: 'Convert Type' },
                { value: 'uppercase', name: 'To Uppercase' },
                { value: 'lowercase', name: 'To Lowercase' },
                { value: 'trim', name: 'Trim Whitespace' },
                { value: 'copy', name: 'Copy to New Field' },
              ],
            },
            {
              key: 'name',
              label: 'Field Name',
              type: 'string',
              required: true,
              placeholder: 'e.g., email',
              description: 'Name of the field to operate on',
            },
            {
              key: 'newName',
              label: 'New Field Name',
              type: 'string',
              placeholder: 'e.g., emailAddress',
              description: 'New name for the field',
              displayOptions: {
                show: { operation: ['rename', 'copy', 'set'] },
              },
            },
            {
              key: 'fieldType',
              label: 'Value Type',
              type: 'options',
              default: 'string',
              options: [
                { value: 'string', name: 'String' },
                { value: 'number', name: 'Number' },
                { value: 'boolean', name: 'Boolean' },
                { value: 'json', name: 'Object/JSON' },
              ],
              displayOptions: {
                show: { operation: ['set'] },
              },
            },
            {
              key: 'value',
              label: 'Value',
              type: 'string',
              placeholder: '{{ $node["NodeName"].string }}',
              description: 'Value to set (supports expressions)',
              displayOptions: {
                show: {
                  operation: ['set'],
                  fieldType: ['string'],
                },
              },
            },
            {
              key: 'numberValue',
              label: 'Value',
              type: 'number',
              default: 0,
              displayOptions: {
                show: {
                  operation: ['set'],
                  fieldType: ['number'],
                },
              },
            },
            {
              key: 'booleanValue',
              label: 'Value',
              type: 'boolean',
              default: false,
              displayOptions: {
                show: {
                  operation: ['set'],
                  fieldType: ['boolean'],
                },
              },
            },
            {
              key: 'jsonValue',
              label: 'Value (JSON)',
              type: 'json',
              default: '{}',
              displayOptions: {
                show: {
                  operation: ['set'],
                  fieldType: ['json'],
                },
              },
            },
            {
              key: 'toType',
              label: 'Convert To',
              type: 'options',
              default: 'string',
              options: [
                { value: 'string', name: 'String' },
                { value: 'number', name: 'Number' },
                { value: 'integer', name: 'Integer' },
                { value: 'boolean', name: 'Boolean' },
                { value: 'datetime', name: 'Date/Time (ISO)' },
                { value: 'json', name: 'JSON Object' },
                { value: 'array', name: 'Array' },
              ],
              displayOptions: {
                show: { operation: ['convert'] },
              },
            },
            {
              key: 'expression',
              label: 'Expression',
              type: 'string',
              placeholder: '$.nested.field',
              description: 'JSONPath expression to extract value from input',
              displayOptions: {
                show: { operation: ['set'] },
              },
            },
          ],
        },
      ],
    },

    // ===== JSON MODE =====
    {
      key: 'jsonData',
      label: 'JSON Output',
      type: 'json',
      required: true,
      default: '{\n  "newField": "value"\n}',
      description: 'Define output fields as JSON. Supports {{ }} expressions.',
      displayOptions: {
        show: { mode: ['json'] },
      },
    },

    // ===== OPTIONS =====
    {
      key: 'includeOtherFields',
      label: 'Include Other Input Fields',
      type: 'boolean',
      default: true,
      description: 'Include fields from input that are not explicitly modified',
    },
  ],
};

export default schema;
